import { useState, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { Modal } from '@mui/material';
import { cn, getContentKeyAccToLang, showToast } from '../../../utils/utils';
import { setCorpLoading } from '../../../store/slices/corporateSlice';
import {
  getBankInformationDropdowns,
  getBankInformationFormData,
  getCorpKycKSABank,
  getCorpKycNonKSABank,
  getInternationalBankInformationFormData,
  saveBankInformation,
  saveCorpKycKSABank,
  saveCorpKycNonKSABank,
  saveInternationBankInformation,
} from '../../../store/actions/corporateAction';
import {
  bank_information_schema,
  bank_information_validation_schema,
  bank_soure_validation_schema,
} from '../../../validations_schemas/bank_information_schema';
import {
  Chips,
  ErrorText,
  RowView,
  Text,
  ViewItems,
} from '../components';
import Dropdown from '../../../components/Dropdown';
import CustomInput from '../../../components/CustomInput';
import Button from '../../../components/Button';
import { fontSizes } from '../../../constants/constants';
import icons from '../../../constants/icons';
import dynamicfunction from '../../../utils/dynamicfunction';
import { useNavigate } from 'react-router';
import { logoutUser } from '../../../store/actions/authActions';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const BankInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isArabic, TextWeight } = dynamicfunction();

  const { corporate, language } = useSelector((state) => state.content);
  const {
    corporate_post_loading,
    bank_information_dropdowns,
    bank_information_data,
  } = useSelector((state) => state.corporate);
  const {
    client_application_id,
    is_kyc,
    is_ksa_corporate,
  } = useSelector((state) => state.constant);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);
  const listsEdited = useRef(false);

  // After a language switch, keep the banks list the user already edited.
  const setLists = (list) => {
    if (listsEdited.current) return;
    setBanks(list);
  };

  // Pick the bank from the current (refetched) list so its name shows in the selected language.
  const findBank = (id) => bank_information_dropdowns?.bankNames?.find((item) => item?.id === id) || null;

  const getContent = (key) => {
    return getContentKeyAccToLang(
      corporate?.corporate_bank_information?.data?.attributes,
      key,
      isArabic
    );
  };

  useEffect(() => {
    dispatch(setCorpLoading(true));
    dispatch(
      getBankInformationDropdowns((data) => {
        is_ksa_corporate
          ? dispatch(
            is_kyc ?
              getCorpKycKSABank(client_application_id, (res) => {
                const newBanks = res?.result?.banks?.map((item) => ({
                  ...item,
                  bankId: data?.result?.bankNames?.find(
                    (b) => b?.id === item?.bankId
                  ),
                }));
                setLists(newBanks);
              }) :
              getBankInformationFormData(client_application_id, (res) => {
                const newBanks = res?.result?.banks?.map((item) => ({
                  ...item,
                  bankId: data?.result?.bankNames?.find(
                    (b) => b?.id === item?.bankId
                  ),
                }));
                setLists(newBanks);
              }),
          )
          : dispatch(
            is_kyc ?
              getCorpKycNonKSABank(client_application_id, (res) => {
                setLists(res?.result?.internationalBanks);
              }) :
              getInternationalBankInformationFormData(client_application_id, (res) => {
                setLists(res?.result?.internationalBanks);
              }),
          );
      })
    );
  }, [language]);

  const onSubmit = (values) => {
    if (banks?.length < 1) {
      showToast({
        icon: 'error',
        title: getContent('add_atleast_one_bank'),
      });
      return;
    }

    const api_data = {
      corpClientApplicationId: client_application_id,
      sourceOfIncomeId: values?.source,
      requestModel: banks,
    };

    if (is_ksa_corporate) {
      api_data.requestModel = banks?.map((item) => ({
        ...item,
        bankName: null,
        bankId: item?.bankId?.id,
      }));
    }

    is_ksa_corporate
      ? dispatch(
        is_kyc ?
          saveCorpKycKSABank(api_data, () => {
            values?.isSaveExist
              ? navigate('/funds/available-funds')
              : onDone();
          }) :
          saveBankInformation(api_data, () => {
            values?.isSaveExist
              ? dispatch(logoutUser())
              : onDone();
          }),
      )
      : dispatch(
        is_kyc ?
          saveCorpKycNonKSABank(api_data, () => {
            values?.isSaveExist
              ? navigate('/funds/available-funds')
              : onDone();
          }) :
          saveInternationBankInformation(api_data, () => {
            values?.isSaveExist
              ? dispatch(logoutUser())
              : onDone();
          }),
      );
  };

  const handleDeleteBank = (index) => {
    const updatedBanks = banks.filter((_, i) => i !== index);
    listsEdited.current = true;
    setBanks(updatedBanks);
  };

  const handleAddBank = (values, resetForm) => {
    const exist = banks?.find((bank) => bank?.iban === values?.iban);
    const swiftExist =
      !is_ksa_corporate && banks?.find((bank) => bank?.swiftCode === values?.swiftCode);

    if (exist) {
      showToast({
        icon: 'error',
        title: getContent('iban_already_exist'),
      });
      return;
    }
    if (swiftExist) {
      showToast({
        icon: 'error',
        title: getContent('swift_code_already_exist'),
      });
      return;
    }
    listsEdited.current = true;
    setBanks(
      banks?.length > 0
        ? [
          ...banks,
          { ...values, corpClientApplicationId: client_application_id },
        ]
        : [{ ...values, corpClientApplicationId: client_application_id }]
    );
    resetForm();
  };

  return (
    <>
      {banks?.length < 5 && (
        <Formik
          initialValues={{
            ...bank_information_schema,
            isSaudi: is_ksa_corporate ?? '',
            iban: is_ksa_corporate ? 'SA' : '',
          }}
          validationSchema={bank_information_validation_schema}
          onSubmit={(values, functions) => {
            handleAddBank(values, functions?.resetForm);
          }}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({
            handleSubmit,
            setFieldValue,
            values,
            resetForm,
            setFieldError,
            errors,
            touched,
            setFieldTouched
          }) => {
            return (
              <div>
                <RowView className='gap-3 justify-end mb-4'>
                  <Button
                    text={getContent('add')}
                    color={'text-white hover:text-primary '}
                    bgColor='bg-primary hover:bg-transparent hover:border-primary'
                    className={
                      'border border-transparent w-44 px-0 text-center'
                    }
                    onClick={handleSubmit}
                  />
                  <Button
                    text={getContent('clear')}
                    color={'text-primary hover:text-white'}
                    bgColor='bg-white hover:bg-primary'
                    className={'border border-primary w-44 px-0 text-center'}
                    onClick={resetForm}
                  />
                </RowView>

                <RowView className='gap-7' isResponsive>
                  {is_ksa_corporate ? (
                    <ViewItems className={'w-full'}>
                      <Dropdown
                        label={getContent('bank_name')}
                        selectedValue={findBank(values?.bankId?.id)}
                        valueKey='name'
                        onSelect={(val) => {
                          setFieldValue('bankId', val);
                          setFieldTouched('bankId', false, false);
                        }}
                        isRequired
                        placeholder={getContent('placeholder_bank_name')}
                        options={bank_information_dropdowns?.bankNames || []}
                        errorMessage={getContent(touched?.bankId ? errors?.bankId : '')}
                      />
                    </ViewItems>
                  ) : (
                    <ViewItems className={'w-full'}>
                      <CustomInput
                        label={getContent('bank_name')}
                        isRounded
                        value={values?.bankName}
                        onChange={(e) => {
                          setFieldValue('bankName', e.target.value);
                          setFieldTouched('bankName', true, false);
                        }}
                        isRequired
                        placeholder={getContent('placeholder_bank_name')}
                        errorMessage={getContent(touched?.bankName ? errors?.bankName : '')}
                      />
                    </ViewItems>
                  )}
                  <ViewItems className={'w-full'}>
                    <CustomInput
                      label={getContent('branch')}
                      isRounded
                      value={values?.branch}
                      onChange={(e) => {
                        setFieldValue('branch', e.target.value);
                        setFieldTouched('branch', true, false);
                      }}
                      isRequired
                      placeholder={getContent('placeholder_branch')}
                      errorMessage={getContent(touched?.branch ? errors?.branch : '')}
                    />
                  </ViewItems>
                  <ViewItems className={'w-full'}>
                    <CustomInput
                      label={getContent('beneficiary_name')}
                      isRounded
                      value={values?.beneficiaryName}
                      onChange={(e) => {
                        setFieldValue('beneficiaryName', e.target.value);
                        setFieldTouched('beneficiaryName', true, false);
                      }}
                      isRequired
                      placeholder={getContent('placeholder_beneficiary_name')}
                      errorMessage={getContent(touched?.beneficiaryName ? errors?.beneficiaryName : '')}
                    />
                  </ViewItems>
                </RowView>

                <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                  <ViewItems className={'w-full'}>
                    <CustomInput
                      label={getContent('main_account_aumber_iban')}
                      isRounded
                      value={values?.iban}
                      onChange={(e) => {
                        if (!e.target.value.startsWith('SA') && values?.isSaudi) {
                          e.target.value = 'SA';
                        }
                        setFieldValue('iban', e.target.value?.toUpperCase());
                        setFieldTouched('iban', true, false);
                      }}
                      isRequired
                      placeholder={getContent(
                        'placeholder_main_account_aumber_iban'
                      )}
                      labelDivClass={"h-6 md:h-9 lg:h-6"}
                      errorMessage={getContent(touched?.iban ? errors?.iban : '')}
                      maxLength={values?.isSaudi ? 24 : 34}
                    />
                  </ViewItems>
                  {!is_ksa_corporate && (
                    <>
                      <ViewItems className={'w-full'}>
                        <CustomInput
                          label={getContent('registered_address_branch')}
                          isRounded
                          value={values?.registeredAddress}
                          onChange={(e) => {
                            setFieldValue('registeredAddress', e.target.value);
                            setFieldTouched('registeredAddress', true, false);
                          }}
                          isRequired
                          placeholder={getContent(
                            'placeholder_registered_address_branch'
                          )}
                          labelDivClass={"h-9 sm:h-6 md:h-9 lg:h-6"}
                          errorMessage={getContent(touched?.registeredAddress ? errors?.registeredAddress : '')}
                        />
                      </ViewItems>
                      <ViewItems className={'w-full'}>
                        <CustomInput
                          label={getContent('aba_routing_number')}
                          isRounded
                          value={values?.abaRoutingNumber}
                          onChange={(e) => {
                            if (/^\d*$/.test(e.target.value)) {
                              setFieldValue('abaRoutingNumber', e.target.value);
                              setFieldTouched('abaRoutingNumber', true, false);
                            }
                          }}
                          isRequired
                          maxLength={20}
                          placeholder={getContent(
                            'placeholder_aba_routing_number'
                          )}
                          labelDivClass={"h-9 sm:h-6 md:h-9 lg:h-6"}
                          errorMessage={getContent(touched?.abaRoutingNumber ? errors?.abaRoutingNumber : '')}
                        />
                      </ViewItems>
                    </>
                  )}
                </RowView>

                {!is_ksa_corporate && (
                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <ViewItems className={'w-full'}>
                      <CustomInput
                        label={getContent('swift_code')}
                        isRounded
                        value={values?.swiftCode}
                        onChange={(e) => {
                          setFieldValue('swiftCode', e.target.value?.toUpperCase());
                          setFieldTouched('swiftCode', true, false);
                        }}
                        isRequired
                        placeholder={getContent('placeholder_swift_code')}
                        errorMessage={getContent(touched?.swiftCode ? errors?.swiftCode : '')}
                        maxLength={11}
                      />
                    </ViewItems>
                  </RowView>
                )}
              </div>
            );
          }}
        </Formik>
      )}

      {banks && banks.length > 0 && (
        <div className='border border-border rounded-lg mt-4 min-w-full overflow-x-auto'>
          <RowView className='bg-primary/10 px-4 py-3 gap-2 justify-between w-max min-w-full'>
            <RowView className={'gap-2'}>
              <Text
                text={getContent('bank_name')}
                color='text-primary'
                className='min-w-[150px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              <Text
                text={getContent('branch')}
                color='text-primary'
                className='min-w-[150px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              <Text
                text={getContent('beneficiary_name')}
                color='text-primary'
                className='min-w-[200px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              <Text
                text={getContent('main_account_aumber_iban')}
                color='text-primary'
                className='min-w-[300px] max-w-[300px]'
                weight={TextWeight.medium}
                isTruncate={false}
                size={fontSizes.h14}
              />
              {!is_ksa_corporate && (
                <>
                  <Text
                    text={getContent('registered_address_branch')}
                    color='text-primary'
                    className='min-w-[300px] max-w-[300px]'
                    weight={TextWeight.medium}
                    isTruncate={false}
                    size={fontSizes.h14}
                  />
                  <Text
                    text={getContent('aba_routing_number')}
                    color='text-primary'
                    className='min-w-[300px] max-w-[300px]'
                    weight={TextWeight.medium}
                    isTruncate={false}
                    size={fontSizes.h14}
                  />
                  <Text
                    text={getContent('swift_code')}
                    color='text-primary'
                    className='min-w-[150px] max-w-[150px]'
                    weight={TextWeight.medium}
                    isTruncate={false}
                    size={fontSizes.h14}
                  />
                </>
              )}
            </RowView>
            <Text
              text={getContent('action')}
              color='text-primary'
              align={cn(isArabic ? 'mr-auto text-left' : 'ml-auto text-right')}
              className={'min-w-[60px] max-w-[60px]'}
              weight={TextWeight.medium}
              isTruncate={false}
              size={fontSizes.h14}
            />
          </RowView>
          {banks?.map((bank, index) => (
            <RowView
              key={index}
              className={'px-4 py-3 border-b border-b-border gap-2 justify-between w-max min-w-full'}
            >
              <RowView className={'gap-2'}>
                <Text
                  text={findBank(bank?.bankId?.id)?.name || bank?.bankId?.name || bank?.bankName}
                  color='text-primary'
                  className='min-w-[150px] max-w-[150px]'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                <Text
                  text={bank?.branch}
                  color='text-primary'
                  className='min-w-[150px] max-w-[150px]'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                <Text
                  text={bank?.beneficiaryName}
                  color='text-primary'
                  className='min-w-[200px] max-w-[200px]'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                <Text
                  text={bank?.iban}
                  color='text-primary'
                  className='min-w-[300px] max-w-[300px]'
                  weight={TextWeight.medium}
                  isTruncate={false}
                  size={fontSizes.h14}
                />
                {!is_ksa_corporate && (
                  <>
                    <Text
                      text={bank?.registeredAddress}
                      color='text-primary'
                      className='min-w-[300px] w-[300px]'
                      weight={TextWeight.medium}
                      isTruncate={false}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={bank?.abaRoutingNumber}
                      color='text-primary'
                      className='min-w-[300px] w-[300px]'
                      weight={TextWeight.medium}
                      isTruncate={false}
                      size={fontSizes.h14}
                    />
                    <Text
                      text={bank?.swiftCode}
                      color='text-primary'
                      className='min-w-[150px] max-w-[150px]'
                      weight={TextWeight.medium}
                      isTruncate={false}
                      size={fontSizes.h14}
                    />
                  </>
                )}
              </RowView>
              <RowView className='min-w-[60px] max-w-[60px] justify-end gap-4 flex-shrink-0'>
                <ReactSVG
                  src={icons.delete_icon}
                  className='cursor-pointer'
                  onClick={() => handleDeleteBank(index)}
                />
                <ReactSVG
                  src={icons.edit_icon}
                  className='cursor-pointer'
                  onClick={() => {
                    setSelectedBank({ ...bank, index });
                    setIsModalOpen(true);
                  }}
                />
              </RowView>
            </RowView>
          ))}
        </div>
      )}

      <Formik
        enableReinitialize
        initialValues={draftValues.current ? draftValues.current : {
          source: bank_information_data?.sourceOfIncomeId ?? [],
          isSaveExist: false,
        }}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validationSchema={bank_soure_validation_schema}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          handleSubmit,
          setFieldValue,
          values,
          resetForm,
          setFieldError,
          touched,
          setFieldTouched,
          errors,
        }) => (
          <>
            <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
            <div className='w-full h-0.5 bg-border my-3 sm:mt-5 mb-3'></div>
            <Text
              text={getContent('source_of_income_heading')}
              size={fontSizes.h22}
              isTruncate={false}
              weight={TextWeight.semiBold}
              className={'block'}
              isRequired
            />
            <Chips
              label={getContent('')}
              required={false}
              value={values?.source}
              onChange={(e) => {
                setFieldValue('source', e);
                setFieldTouched('source', false);
              }}
              isMulti
              options={bank_information_dropdowns?.sourceOfIncome}
            />
            <ErrorText text={getContent(touched?.source ? errors?.source : '')} className='block' />
            <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
            <RowView className='gap-7 pt-3 sm:pt-5'>
              <Button
                text={getContent('save_and_proceed')}
                color={'text-white hover:text-primary'}
                bgColor='bg-primary hover:bg-transparent hover:border-primary'
                className={'border border-transparent w-44 px-0 text-center'}
                isDisabled={corporate_post_loading || !values?.source}
                loading={corporate_post_loading}
                onClick={() => {
                  setFieldValue('isSaveExist', false);
                  handleSubmit();
                }}
              />
              <Button
                text={getContent('save_and_exit')}
                color={'text-primary hover:text-white'}
                bgColor='bg-white hover:bg-primary'
                className={'border border-primary w-44 px-0 text-center'}
                isDisabled={corporate_post_loading || !values?.source}
                onClick={() => {
                  setFieldValue('isSaveExist', true);
                  handleSubmit();
                }}
              />
            </RowView>
          </>
        )}
      </Formik>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className='bg-white rounded-lg p-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 md:w-1/2'>
          <Formik
            enableReinitialize
            initialValues={{
              bankName: selectedBank?.bankName || '',
              bankId: selectedBank?.bankId || '',
              branch: selectedBank?.branch || '',
              beneficiaryName: selectedBank?.beneficiaryName || '',
              iban: selectedBank?.iban || '',
              registeredAddress: selectedBank?.registeredAddress || '',
              abaRoutingNumber: selectedBank?.abaRoutingNumber || '',
              swiftCode: selectedBank?.swiftCode || '',
              isSaudi: is_ksa_corporate,
            }}
            validationSchema={bank_information_validation_schema}
            onSubmit={(values, functions) => {
              const updatedBanks = banks.map((bank, index) => {
                if (index === selectedBank?.index) {
                  return {
                    ...values,
                    corpClientApplicationId: client_application_id,
                  };
                }
                return bank;
              });
              listsEdited.current = true;
              setBanks(updatedBanks);
              setIsModalOpen(false);
              setSelectedBank(null);
              functions.resetForm();
            }}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({
              handleSubmit,
              setFieldValue,
              values,
              resetForm,
              setFieldError,
              touched,
              setFieldTouched,
              errors,
            }) => {
              return (
                <div>
                  <RowView className='item-center justify-between'>
                    <Text
                      text={getContent('edit')}
                      weight={TextWeight.bold}
                      size={fontSizes.h20}
                    />
                    <ReactSVG
                      src={icons.cross}
                      className='cursor-pointer'
                      onClick={() => setIsModalOpen(false)}
                    />
                  </RowView>

                  <RowView className='gap-7 mt-4' isResponsive>
                    {is_ksa_corporate ? (
                      <Dropdown
                        label={getContent('bank_name')}
                        selectedValue={findBank(values?.bankId?.id)}
                        valueKey='name'
                        onSelect={(val) => {
                          setFieldValue('bankId', val);
                          setFieldTouched('bankId', true);
                        }}
                        placeholder={getContent('placeholder_bank_name')}
                        options={bank_information_dropdowns?.bankNames || []}
                        isRequired
                        errorMessage={getContent(touched?.bankId ? errors?.bankId : '')}
                      />
                    ) : (
                      <CustomInput
                        isRounded
                        isRequired={true}
                        label={getContent('bank_name')}
                        value={values?.bankName}
                        onChange={(e) => {
                          setFieldValue('bankName', e.target.value);
                          setFieldTouched('bankName', true, false);
                        }}
                        placeholder={getContent('placeholder_bank_name')}
                        errorMessage={getContent(touched?.bankName ? errors?.bankName : '')}
                      />
                    )}
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('branch')}
                      value={values?.branch}
                      onChange={(e) => {
                        setFieldValue('branch', e.target.value);
                        setFieldTouched('branch', true, false);
                      }}
                      placeholder={getContent('placeholder_branch')}
                      errorMessage={getContent(touched?.branch ? errors?.branch : '')}
                    />
                  </RowView>

                  <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('beneficiary_name')}
                      value={values?.beneficiaryName}
                      onChange={(e) => {
                        setFieldValue('beneficiaryName', e.target.value);
                        setFieldTouched('beneficiaryName', true, false);
                      }}
                      labelDivClass={'h-6 md:h-9 lg:h-6'}
                      placeholder={getContent('placeholder_beneficiary_name')}
                      errorMessage={getContent(touched?.beneficiaryName ? errors?.beneficiaryName : '')}
                    />
                    <CustomInput
                      isRounded
                      isRequired={true}
                      label={getContent('main_account_aumber_iban')}
                      value={values?.iban}
                      onChange={(e) => {
                        setFieldValue('iban', e.target.value?.toUpperCase());
                        setFieldTouched('iban', true, false);
                      }}
                      labelDivClass={'h-6 md:h-9 lg:h-6'}
                      placeholder={getContent(
                        'placeholder_main_account_aumber_iban'
                      )}
                      errorMessage={getContent(touched?.iban ? errors?.iban : '')}
                      maxLength={values?.isSaudi ? 24 : 32}
                    />
                  </RowView>

                  {!is_ksa_corporate && (
                    <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                      <CustomInput
                        isRounded
                        isRequired={true}
                        label={getContent('registered_address_branch')}
                        value={values?.registeredAddress}
                        onChange={(e) => {
                          setFieldValue('registeredAddress', e.target.value);
                          setFieldTouched('registeredAddress', true, false);
                        }}
                        labelDivClass={'h-6 md:h-9 lg:h-10 xl:h-6'}
                        placeholder={getContent(
                          'placeholder_registered_address_branch'
                        )}
                        errorMessage={getContent(touched?.registeredAddress ? errors?.registeredAddress : '')}
                      />
                      <CustomInput
                        isRounded
                        isRequired={true}
                        label={getContent('aba_routing_number')}
                        value={values?.abaRoutingNumber}
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            setFieldValue('abaRoutingNumber', e.target.value);
                            setFieldTouched('abaRoutingNumber', true, false);
                          }
                        }}
                        labelDivClass={'h-6 md:h-9 lg:h-10 xl:h-6'}
                        maxLength={20}
                        placeholder={getContent(
                          'placeholder_aba_routing_number'
                        )}
                        errorMessage={getContent(touched?.abaRoutingNumber ? errors?.abaRoutingNumber : '')}
                      />
                    </RowView>
                  )}

                  {!is_ksa_corporate && (
                    <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                      <CustomInput
                        isRounded
                        isRequired={true}
                        label={getContent('swift_code')}
                        value={values?.swiftCode}
                        onChange={(e) => {
                          setFieldValue(
                            'swiftCode',
                            e.target.value?.toUpperCase()
                          );
                          setFieldTouched('swiftCode', true, false);
                        }}
                        placeholder={getContent('placeholder_swift_code')}
                        errorMessage={getContent(touched?.swiftCode ? errors?.swiftCode : '')}
                        maxLength={11}
                      />
                    </RowView>
                  )}
                  <RowView className='gap-7 pt-3 sm:pt-5'>
                    <Button
                      text={getContent('submit')}
                      color={'text-white hover:text-primary '}
                      bgColor='bg-primary hover:bg-transparent hover:border-primary'
                      className={
                        'border border-transparent w-1/3 mx-auto px-0 text-center'
                      }
                      onClick={handleSubmit}
                    />
                  </RowView>
                </div>
              );
            }}
          </Formik>
        </div>
      </Modal>
    </>
  );
};

export default BankInformation;
